import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Chip, Dialog, DialogTitle, IconButton, LinearProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import { Link as RouterLink } from "react-router-dom";
import bkstApi from "../../api";
import KitchenOrder, { KitchenOrderMetaRow } from "./KitchenOrder";
import { UserContext } from "../../context/UserContext";
import {
  getItemProgress,
  getItemProgressStageLabel,
  mergeItemProgress,
} from "../../common/util";

function canOpenFullOrder(role) {
  return role !== "kitchen" && role !== "clerk";
}

export default function ProductionTrackerItemDialog(props) {
  const { open, orderId, itemIndex, onClose, onItemProgressChange, onFocusItemChange } = props;
  const { user } = useContext(UserContext);
  const progressSteps = user?.config?.kds?.progressSteps;

  const [order, setOrder] = useState();
  const [loading, setLoading] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollTop = 0;
    }
  }, [open, itemIndex]);

  useEffect(() => {
    if (!open || !orderId) {
      return;
    }
    setLoading(true);
    setOrder(undefined);
    bkstApi(`/my/order/${orderId}`)
      .then((res) => setOrder(res.data))
      .catch(() => {
        onClose();
      })
      .finally(() => setLoading(false));
  }, [open, orderId, onClose]);

  const handleItemProgressChange = ({ items }) => {
    setOrder((prev) => ({ ...prev, items: mergeItemProgress(prev.items, items) }));
    onItemProgressChange?.(orderId, { items });
  };

  const focusIndex = Number(itemIndex);
  const focusItem =
    order?.items && focusIndex >= 0 && focusIndex < order.items.length
      ? order.items[focusIndex]
      : undefined;
  const focusProgress = focusItem ? getItemProgress(order, focusItem, progressSteps) : null;
  const progressStageLabel = focusProgress ? getItemProgressStageLabel(focusProgress) : "";

  const viewFullOrderHref =
    order && canOpenFullOrder(user?.role) ? `/order/${orderId}` : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle component="div" sx={{ pr: 14, flexShrink: 0, py: 1.5, position: "relative" }}>
        {order ? (
          <KitchenOrderMetaRow
            order={order}
            trailing={
              progressStageLabel ? (
                <Chip
                  size="small"
                  color="info"
                  label={progressStageLabel}
                  sx={{ textTransform: "uppercase", flexShrink: 0, fontWeight: 600 }}
                />
              ) : null
            }
          />
        ) : null}
        <Box
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {viewFullOrderHref && (
            <IconButton
              component={RouterLink}
              to={viewFullOrderHref}
              aria-label="View Order"
              color="secondary"
            >
              <ReadMoreIcon />
            </IconButton>
          )}
          <IconButton onClick={onClose} aria-label="Close item details">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: "1 1 auto",
          overflow: "auto",
          minHeight: 0,
          bgcolor: "#F7F7F7",
        }}
      >
        {loading || !order ? (
          <LinearProgress />
        ) : (
          <Box px={2} pb={3}>
            <KitchenOrder
              order={order}
              focusIndex={focusIndex}
              onItemProgressChange={handleItemProgressChange}
              onFocusItemChange={onFocusItemChange}
              hideOrderMeta
              hideStatusAlert
            />
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
